(() => {
  'use strict';
  if (customElements.get('kindred-core-hover')) return;

  const styleRequests = new Map();
  let viewerRequest;
  const assetURL = source => {
    const url = new URL(source, document.baseURI);
    if (!['https:', 'http:'].includes(url.protocol)) throw new Error('Invalid asset URL');
    return url.href;
  };
  const loadStyle = source => {
    const href = assetURL(source);
    if (styleRequests.has(href)) return styleRequests.get(href);
    if (Array.from(document.querySelectorAll('link[rel="stylesheet"]')).some(link => link.href === href)) return Promise.resolve();
    const request = new Promise((resolve, reject) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet'; link.href = href;
      link.onload = resolve;
      link.onerror = () => { styleRequests.delete(href); link.remove(); reject(new Error('Viewer stylesheet unavailable')); };
      document.head.append(link);
    });
    styleRequests.set(href, request);
    return request;
  };
  const loadViewer = source => {
    if (customElements.get('kindred-core-viewer')) return Promise.resolve();
    if (viewerRequest) return viewerRequest;
    const src = assetURL(source);
    viewerRequest = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src; script.async = true;
      script.onload = () => {
        if (customElements.get('kindred-core-viewer')) resolve();
        else { viewerRequest = null; script.remove(); reject(new Error('Viewer could not start')); }
      };
      script.onerror = () => { viewerRequest = null; script.remove(); reject(new Error('Viewer script unavailable')); };
      document.head.append(script);
    });
    return viewerRequest;
  };

  class KindredCoreHover extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.generation = (this.generation || 0) + 1;
      this.link = this.closest('[data-kindred-core-link], .grid-product__link');
      if (!this.link) return;
      this.events = new AbortController();
      this.desktop = matchMedia('(min-width: 769px)');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.pointer = false; this.focused = false; this.inViewport = false; this.active = false; this.preparing = false;
      this.posterReady = false; this.viewerReady = false;
      this.photo = this.link.querySelector('[data-kindred-core-photo]');
      this.calibration = null;
      try {
        const frame = JSON.parse(this.dataset.photoFrame || 'null');
        if (frame && Number.isFinite(frame.width) && frame.width > 0 && Number.isFinite(frame.height) && frame.height > 0 && Array.isArray(frame.bounds) && frame.bounds.length === 4 && frame.bounds.every(Number.isFinite) && frame.bounds[0] >= 0 && frame.bounds[1] >= 0 && frame.bounds[2] <= frame.width && frame.bounds[3] <= frame.height && frame.bounds[2] > frame.bounds[0] && frame.bounds[3] > frame.bounds[1]) this.calibration = frame;
      } catch {}
      this.setAttribute('aria-hidden', 'true'); this.setAttribute('inert', '');
      const options = { signal: this.events.signal };
      const poster = this.querySelector('.kindred-core-hover__poster');
      const generation = this.generation;
      const readyPoster = async () => {
        if (!poster?.naturalWidth) return;
        try { await poster.decode?.(); } catch { return; }
        if (!this.connected || generation !== this.generation) return;
        this.posterReady = true; this.reveal();
      };
      poster?.addEventListener('load', readyPoster, options);
      if (poster?.complete) readyPoster();
      this.link.addEventListener('pointerenter', event => {
        if (event.pointerType && !['mouse', 'pen'].includes(event.pointerType)) return;
        this.pointer = true; this.update();
      }, options);
      this.link.addEventListener('pointerleave', () => { this.pointer = false; this.update(); }, options);
      this.link.addEventListener('focusin', () => { this.focused = this.link.matches(':focus-visible'); this.update(); }, options);
      this.link.addEventListener('focusout', event => {
        if (!event.relatedTarget || !this.link.contains(event.relatedTarget)) { this.focused = false; this.update(); }
      }, options);
      document.addEventListener('visibilitychange', () => this.update(), options);
      this.desktop.addEventListener?.('change', () => this.update(), options);
      this.reduced.addEventListener?.('change', () => this.update(), options);
      this.photo?.addEventListener('load', () => this.updateFrame(), options);
      window.addEventListener('resize', () => this.updateFrame(), options);
      this.resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => this.updateFrame());
      this.resizeObserver?.observe(this);
      if (this.photo) this.resizeObserver?.observe(this.photo);
      this.observer = typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver(entries => {
        this.inViewport = entries[0].isIntersecting; this.update();
      }, { threshold: 0.01 });
      if (this.observer) this.observer.observe(this.link);
      else this.inViewport = true;
      this.updateFrame();
      loadStyle(this.dataset.hoverStyle).catch(() => {});
    }

    disconnectedCallback() {
      this.connected = false; this.generation++;
      this.events?.abort(); this.observer?.disconnect(); this.resizeObserver?.disconnect();
      this.disposeViewer();
      this.removeAttribute('data-kindred-hover-active');
    }

    disposeViewer() {
      const viewer = this.viewer;
      if (!viewer) return;
      viewer.setPlaying(false);
      this.cachedScene = viewer.scene || this.cachedScene;
      const gl = viewer.gl;
      viewer.remove();
      gl?.getExtension('WEBGL_lose_context')?.loseContext();
      this.viewer = null;
      this.viewerReady = false;
      this.reveal();
    }

    update() {
      if (!this.connected) return;
      this.active = this.desktop.matches && this.inViewport && !document.hidden && (this.pointer || this.focused);
      this.reveal();
      if (this.active) this.updateFrame();
      if (!this.active || this.reduced.matches) {
        this.disposeViewer();
        return;
      }
      if (this.viewer) this.viewer.setPlaying(true);
      else this.prepare();
    }

    reveal() {
      this.toggleAttribute('data-kindred-hover-active', this.connected && this.active && (this.posterReady || this.viewerReady));
    }

    updateFrame() {
      const frame = this.calibration, photo = this.photo;
      if (!frame || !photo) return;
      const host = this.getBoundingClientRect(), box = photo.getBoundingClientRect();
      if (host.width <= 0 || host.height <= 0 || box.width <= 0 || box.height <= 0) return;
      const style = getComputedStyle(photo), contain = Math.min(box.width / frame.width, box.height / frame.height);
      const fit = style.objectFit;
      const scale = fit === 'cover' ? Math.max(box.width / frame.width, box.height / frame.height) : fit === 'none' ? 1 : fit === 'scale-down' ? Math.min(1, contain) : contain;
      const scaleX = fit === 'fill' ? box.width / frame.width : scale;
      const scaleY = fit === 'fill' ? box.height / frame.height : scale;
      const imageWidth = frame.width * scaleX, imageHeight = frame.height * scaleY;
      const position = style.objectPosition.split(/\s+/);
      const offset = (value, space, fallback) => {
        if (value === 'left' || value === 'top') return 0;
        if (value === 'right' || value === 'bottom') return space;
        if (value === 'center') return space / 2;
        if (/^-?[\d.]+%$/.test(value)) return space * parseFloat(value) / 100;
        if (/^-?[\d.]+px$/.test(value)) return parseFloat(value);
        return fallback;
      };
      const left = box.left - host.left + offset(position[0], box.width - imageWidth, (box.width - imageWidth) / 2);
      const top = box.top - host.top + offset(position[1], box.height - imageHeight, (box.height - imageHeight) / 2);
      const bounds = frame.bounds;
      this.photoFrame = { cx: left + (bounds[0] + bounds[2]) * scaleX / 2, cy: top + (bounds[1] + bounds[3]) * scaleY / 2, height: (bounds[3] - bounds[1]) * scaleY };
      const posterLeft = this.photoFrame.cx - (bounds[0] + bounds[2]) * scaleY / 2;
      for (const poster of this.querySelectorAll('.kindred-core-hover__poster, .kindred-core-poster')) {
        poster.style.left = `${posterLeft}px`; poster.style.top = `${top}px`;
        poster.style.width = `${frame.width * scaleY}px`; poster.style.height = `${imageHeight}px`;
      }
      if (this.viewer) { this.viewer.photoFrame = this.photoFrame; this.viewer.schedule(); }
    }

    async prepare() {
      if (this.preparing) return;
      this.preparing = true;
      const generation = this.generation;
      try {
        await Promise.all([loadStyle(this.dataset.viewerStyle), loadStyle(this.dataset.hoverStyle), loadViewer(this.dataset.viewerScript)]);
        if (!this.connected || generation !== this.generation || !this.active || this.reduced.matches) return;
        const viewer = document.createElement('kindred-core-viewer');
        viewer.dataset.scene = this.dataset.scene;
        viewer.dataset.poster = this.dataset.poster;
        viewer.dataset.mode = 'hover';
        viewer.photoFrame = this.photoFrame;
        if (this.cachedScene) viewer.scene = this.cachedScene;
        if (this.dataset.labels) viewer.dataset.labels = this.dataset.labels;
        viewer.setAttribute('aria-hidden', 'true'); viewer.setAttribute('inert', '');
        viewer.addEventListener('kindred-core-ready', () => {
          if (this.viewer !== viewer || !this.connected) return;
          this.viewerReady = true; this.reveal();
        }, { signal: this.events.signal });
        viewer.addEventListener('kindred-core-unavailable', () => {
          if (this.viewer !== viewer || !this.connected) return;
          this.viewerReady = false; this.reveal();
        }, { signal: this.events.signal });
        this.viewer = viewer;
        this.append(viewer);
        this.updateFrame();
        viewer.querySelectorAll('button, canvas').forEach(element => { element.tabIndex = -1; });
        viewer.setPlaying(true);
      } catch {
        if (generation === this.generation) this.setAttribute('data-kindred-hover-state', 'poster');
      } finally {
        if (generation === this.generation) this.preparing = false;
      }
    }
  }

  customElements.define('kindred-core-hover', KindredCoreHover);
})();
